<template>
  <div class="relative">
    <div class="custom-swipe" @touchstart="onTouchStart" @touchmove="onTouchMove" @touchend="onTouchEnd"
      :style="{ width: width, height: height }">
      <div class="swipe-track" :style="{
        transform: `translateX(-${translateXPercent}%)`,
        transition: isTransition ? 'transform 0.3s ease' : 'none'
      }">
        <!-- 循环列表：首尾各加一个副本 -->
        <div v-for="(item, index) in loopList" :key="index" class="swipe-item">
          <slot :item="getOriginalItem(item, index)" :index="getOriginalIndex(index)">
            <div class="swipe-content">{{ item }}</div>
          </slot>
        </div>
      </div>
    </div>
    <!-- 指示器 -->
    <div v-if="showIndicators && realListLength > 1" class="swipe-indicators" :style="{ 'bottom': bottom,'width': indicatorWidth,'left': indicatorLeft }">
      <div v-for="(_item, index) in realList" :key="index" class="indicator" :class="{ active: index === realIndex }"
        @click="goTo(index)" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
interface Props {
  width?: any
  list?: any[]
  height?: any
  autoplay?: number // 自动播放间隔，单位ms
  loop?: boolean // 是否循环
  showIndicators?: boolean // 是否显示指示器
  initialIndex?: number // 初始索引
  notGo?: boolean // 是否禁止跳转
  bottom?: string // 指示器底部距离
  indicatorWidth?: string // 指示器宽度
  indicatorLeft?: string // 指示器左边距离
}

const props = withDefaults(defineProps<Props>(), {
  width: 180,
  list: () => [],
  height: 150,
  autoplay: 3000,
  loop: true,
  showIndicators: true,
  initialIndex: 0,
  notGo: false,
  indicatorWidth: '90%',
  indicatorLeft: '5%'
})

const router = useRouter()
const route = useRoute()
const emit = defineEmits<{
  change: [index: number]
  click: [index: number]
}>()

const currentIndex = ref(props.initialIndex + 1) // 从1开始，因为前面有一个复制节点
const isTransition = ref(true)
let timer: ReturnType<typeof setInterval> | null = null

// 真实列表长度
const realListLength = computed(() => props.list.length)

// 循环列表：首尾各加一个副本
const loopList = computed(() => {
  if (!props.list.length) return []
  if (props.list.length === 1) return [props.list[0]]

  if (props.loop) {
    // 首尾各加一个副本：[last, ...list, first]
    return [props.list[props.list.length - 1], ...props.list, props.list[0]]
  }
  return [...props.list]
})

// 真实列表（不含复制节点）
const realList = computed(() => props.list)

// 计算当前真实索引（排除首尾复制节点）
const realIndex = computed(() => {
  if (!props.list.length) return 0
  let index = currentIndex.value - 1 // 减去开头的复制节点
  if (index >= props.list.length) {
    index = 0
  }
  return index
})

// 计算translateX的百分比
const translateXPercent = computed(() => {
  return currentIndex.value * 100
})

// 获取原始item（处理循环列表的索引映射）
const getOriginalItem = (item: any, index: number) => {
  if (!props.loop) return item
  // 循环列表的索引：0是最后一个，1~n是真实列表，n+1是第一个
  if (index === 0) return props.list[props.list.length - 1]
  if (index === loopList.value.length - 1) return props.list[0]
  return props.list[index - 1]
}

// 获取原始索引
const getOriginalIndex = (index: number) => {
  if (!props.loop) return index
  if (index === 0) return props.list.length - 1
  if (index === loopList.value.length - 1) return 0
  return index - 1
}

// 开始自动播放
const startAutoplay = () => {
  if (props.autoplay <= 0 || props.list.length <= 1) return
  stopAutoplay()
  timer = setInterval(() => {
    next()
  }, props.autoplay)
}

// 停止自动播放
const stopAutoplay = () => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

const goToChat = (item: any) => {
  if (props.notGo) return
  localStorage.setItem('path', route.path)
  router.push({ name: 'Chat', query: { id: item.id, cover: item?.profile?.cover || '', name: item?.profile?.nickname } })
}

// 下一张
const next = () => {
  if (props.list.length <= 1) return
  isTransition.value = true
  currentIndex.value++

  // 如果到了复制节点，立即跳回真实节点
  if (currentIndex.value >= loopList.value.length - 1) {
    setTimeout(() => {
      isTransition.value = false
      currentIndex.value = 1
    }, 300)
  }

  emit('change', realIndex.value)
}

// 上一张
const prev = () => {
  if (props.list.length <= 1) return
  isTransition.value = true
  currentIndex.value--

  // 如果到了复制节点，立即跳回真实节点
  if (currentIndex.value <= 0) {
    setTimeout(() => {
      isTransition.value = false
      currentIndex.value = loopList.value.length - 2
    }, 300)
  }

  emit('change', realIndex.value)
}

// 跳转到指定索引
const goTo = (index: number) => {
  if (index < 0 || index >= props.list.length) return
  if (index === realIndex.value) return

  isTransition.value = true
  const targetIndex = index + 1 // 加1因为前面有复制节点

  // 如果是循环模式，处理边界情况
  if (props.loop) {
    // 从前面跳到后面（或反之）
    if (index === 0 && realIndex.value === props.list.length - 1) {
      // 从最后一个跳到第一个
      currentIndex.value = targetIndex
      setTimeout(() => {
        isTransition.value = false
        currentIndex.value = 1
      }, 300)
    } else if (index === props.list.length - 1 && realIndex.value === 0) {
      // 从第一个跳到最后一个
      currentIndex.value = targetIndex
      setTimeout(() => {
        isTransition.value = false
        currentIndex.value = loopList.value.length - 2
      }, 300)
    } else {
      currentIndex.value = targetIndex
    }
  } else {
    currentIndex.value = targetIndex
  }

  emit('click', index)
  emit('change', index)
}

// 触摸事件 - 改进：只在纯水平滑动时拦截
let startX = 0
let startY = 0
let isHorizontalSwipe = false
let isIntercepted = false // 是否已拦截本次滑动

const onTouchStart = (e: TouchEvent) => {
  if (!e.touches[0]) return
  startX = e.touches[0].clientX
  startY = e.touches[0].clientY
  isHorizontalSwipe = false
  isIntercepted = false
  stopAutoplay()
}

const onTouchMove = (e: TouchEvent) => {
  if (!e.touches[0] || isIntercepted) return

  const deltaX = e.touches[0].clientX - startX
  const deltaY = e.touches[0].clientY - startY

  // 判断滑动方向：只有当水平距离明显大于垂直距离时才拦截
  // 使用更大的阈值（30px）确保是故意水平滑动
  if (!isHorizontalSwipe && Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 30) {
    isHorizontalSwipe = true
    isIntercepted = true
    // 只有在事件可取消时才调用 preventDefault
    if (e.cancelable) {
      e.preventDefault()
    }

    if (deltaX > 0) {
      prev()
    } else {
      next()
    }
  }
}

const onTouchEnd = () => {
  isHorizontalSwipe = false
  isIntercepted = false
  startAutoplay()
}

// 监听列表变化
watch(() => props.list, (newList) => {
  if (newList.length > 0) {
    // 重置到正确位置
    currentIndex.value = 1
    // 重启自动播放
    startAutoplay()
  }
})

onMounted(() => {
  startAutoplay()
})

onUnmounted(() => {
  stopAutoplay()
})

defineExpose({
  next,
  prev,
  goTo,
  startAutoplay,
  stopAutoplay
})
</script>

<style lang="scss" scoped>
.custom-swipe {
  position: relative;
  width: 130px;
  overflow: hidden;
  border-radius: 8px;
  height: 180px;
}

.swipe-track {
  display: flex;
  height: 100%;
  width: 100%;
}

.swipe-item {
  flex-shrink: 0;
  width: 100%;
  height: 100%;

  .swipe-content {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.swipe-indicators {
  position: absolute;
  bottom: -3%;
  left: 5%;

  display: flex;
  gap: 6px;

  width: 90%;
}

.indicator {
  flex: 1; // ⭐ 关键
  height: 3px;

  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 2px;

  transition: all 0.3s;
}

.indicator.active {
  background: #FFFFFF;
}
</style>
